import axios from 'axios';
import { env } from '@/config/env';
import { TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/config/constants';
import { getItem, setItem, removeItem } from '@/utils/storage.util';

const axiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ─── Pending Request Tracking ────────────────────────────────────────────────
// Every request gets its own AbortController so all in-flight requests can be
// cancelled together (used on logout / session expiry).
const pendingRequests = new Set<AbortController>();
const controllersBySignal = new WeakMap<AbortSignal, AbortController>();

function releaseSignal(config?: { signal?: unknown }) {
  const signal = config?.signal;
  if (!(signal instanceof AbortSignal)) return;
  const controller = controllersBySignal.get(signal);
  if (controller) {
    pendingRequests.delete(controller);
    controllersBySignal.delete(signal);
  }
}

/** Abort every API request that is still in flight. */
export function cancelAllPendingRequests() {
  pendingRequests.forEach(controller => controller.abort());
  pendingRequests.clear();
}

// ─── Request Interceptor ─────────────────────────────────────────────────────
axiosInstance.interceptors.request.use(
  config => {
    const token = getItem<string>(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingRequests.add(controller);
    controllersBySignal.set(controller.signal, controller);
    controller.signal.addEventListener(
      'abort',
      () => {
        pendingRequests.delete(controller);
        controllersBySignal.delete(controller.signal);
      },
      { once: true }
    );
    return config;
  },
  error => Promise.reject(error)
);

// ─── Response Interceptor ────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  response => {
    releaseSignal(response.config);
    return response;
  },
  async error => {
    releaseSignal(error.config);
    const originalRequest = error.config;

    if (error.response?.status === 401 && getItem<string>(REFRESH_TOKEN_KEY) && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getItem<string>(REFRESH_TOKEN_KEY);
        const { data } = await axios.post(`${env.apiBaseUrl}/auth/refresh`, {
          refreshToken,
        });
        const newToken = data.data.accessToken;
        setItem(TOKEN_KEY, newToken);
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return axiosInstance(originalRequest);
      } catch (err) {
        processQueue(err, null);
        removeItem(TOKEN_KEY);
        removeItem(REFRESH_TOKEN_KEY);
        // Signal to the login page that the session expired
        try { sessionStorage.setItem('tc_session_expired', 'true'); } catch { /* ignore */ }
        window.location.href = '/login';
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
